import { Box, Typography } from '@mui/material';
import { useMaterialUIController } from 'context';
import { getCrmColors } from '@layouts/pages/CRM/kanban-board/crm-theme';

export interface TabItem<T extends string> {
    value: T;
    label: string;
    count?: number;
}

interface Props<T extends string> {
    current: T;
    onChange: (value: T) => void;
    tabs: TabItem<T>[];
}

/// Faixa de abas que divide uma tela em seções, cada aba com a mesma fatia da largura.
export function TabBar<T extends string>({ current, onChange, tabs }: Props<T>) {
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);

    return (
        <Box sx={{ display: 'flex', gap: '3px' }}>
            {tabs.map((tab, index) => {
                const active = tab.value === current;

                return (
                    <Box
                        key={tab.value}
                        role="button"
                        tabIndex={0}
                        onClick={() => onChange(tab.value)}
                        onKeyDown={(e) => e.key === 'Enter' && onChange(tab.value)}
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            height: 40,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.9,
                            cursor: 'pointer',
                            fontSize: 13.5,
                            fontWeight: active ? 700 : 600,
                            // Extremos arredondados e meio reto: as abas formam uma peça só.
                            borderRadius: index === 0
                                ? '9px 0 0 9px'
                                : index === tabs.length - 1
                                    ? '0 9px 9px 0'
                                    : 0,
                            color: active ? '#fff' : cc.textSecondary,
                            bgcolor: active ? cc.accent : cc.surfaceBg,
                            border: `1px solid ${active ? cc.accent : cc.border}`,
                            '&:hover': active ? {} : { borderColor: cc.borderHover },
                        }}
                    >
                        {tab.label}

                        {tab.count ? (
                            <Typography
                                sx={{
                                    fontSize: 10.5,
                                    fontWeight: 700,
                                    color: active ? '#fff' : cc.textMuted,
                                    bgcolor: active ? 'rgba(255,255,255,0.25)' : cc.countBg,
                                    borderRadius: '9px',
                                    px: 0.85,
                                    py: 0.1,
                                }}
                            >
                                {tab.count}
                            </Typography>
                        ) : null}
                    </Box>
                );
            })}
        </Box>
    );
}
